import React, { useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Grid,
  SvgIcon
} from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import HomeIcon from '@material-ui/icons/Home';
import KeyValueBadge from 'components/underwrite/acquisition/propertyUnderwrite/header/KeyValueBadge';
import { highlightSearchString } from '../search/SearchCard';
import {
  setQueueDrawerIsOpen
} from 'store/actions';
import { OpportunityStatusChip } from 'components/layout';
import { TaskStatusChip } from 'components/underwrite/postAcquisition';
import { Constants, getModuleByLocation } from 'utilities/constants';
import { CheckTaskCompleted, PostAcqCheckTaskCompleted } from 'services/taskManagementService';
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';
const {
  Routes: { UNDERWRITE_PROPERTY, POSTACQ_TASK },
} = Constants;

const QueueTask = ({
    history,
    location,
    module,
    queueFilterInputLocalState,
    classes,
    bin,
    selectedTxnOpp,
    valuationIsDirty,
    setQueueDrawerIsOpen,
    task,
    showPostAcquisitions
  }) => {
  const taskData = `${task.city}, ${task.state} ${task.zipCode}`;
  const sqftString = task.sqft ? task.sqft.toLocaleString() : '';

  const underwritePath = module === Constants.Module.POSTACQ ? 'task' : 'propuw';
  const locationIsUnderwritePage = location.pathname.split('/')[2] === underwritePath;
  const taskCompleted = module === Constants.Module.POSTACQ ? PostAcqCheckTaskCompleted(task) : CheckTaskCompleted(task, showPostAcquisitions);
  const isSelected = selectedTxnOpp && selectedTxnOpp.id === task.id;
  const { openSaveUWDialog } = useSaveUWDialog();

  const handleListItemClick = (event, task) => {
    if (locationIsUnderwritePage && isSelected) { // If currently viewing the clicked task, just close drawer
      setQueueDrawerIsOpen(false);
    } else {
      const route = `${task.taskType === 'txnOpp' ? UNDERWRITE_PROPERTY : POSTACQ_TASK}/${task.id}`;
      if (valuationIsDirty) {
        openSaveUWDialog({
          saveUWDialogParameters: { route },
          callback: () => setQueueDrawerIsOpen(false)
        });
      } else {
        setQueueDrawerIsOpen(false);
        history.push(route);
      }
    }
  }

  function highlightStatus(text) {
    return highlightSearchString(text, queueFilterInputLocalState, classes);
  }

  const street = useMemo(() => {
    return highlightSearchString(task.street, queueFilterInputLocalState, {
      inlineText: classes.textPrimary,
      inlineSearchText: classes.inlinePrimarySearchText,
    });
  }, [task.street, queueFilterInputLocalState]);

  const taskId = useMemo(() => {
    return highlightSearchString(`${task.id}`, queueFilterInputLocalState, classes);
  }, [task.id, queueFilterInputLocalState]);

  const importName = useMemo(() => {
    return highlightSearchString(task.importName, queueFilterInputLocalState, classes);
  }, [task.importName, queueFilterInputLocalState]);

  const taskTypeString = task.requestType
    ? `${task.channel ? task.channel + ' - ' : ''}${task.stage ? task.stage + ' - ' : ''}${task.requestType}`
    : '';

  const taskType = useMemo(() => {
    return highlightSearchString(taskTypeString, queueFilterInputLocalState, classes);
  }, [taskTypeString, queueFilterInputLocalState]);

  const taskDetails = useMemo(() => {
    return highlightSearchString(taskData, queueFilterInputLocalState, classes);
  }, [taskData, queueFilterInputLocalState]);

  const beds = useMemo(() => {
    return highlightSearchString(`${task.beds}`, queueFilterInputLocalState, classes);
  }, [task.beds, queueFilterInputLocalState]);

  const baths = useMemo(() => {
    return highlightSearchString(`${task.baths}`, queueFilterInputLocalState, classes);
  }, [task.baths, queueFilterInputLocalState]);

  const sqft = useMemo(() => {
    return highlightSearchString(sqftString, queueFilterInputLocalState, classes);
  }, [sqftString, queueFilterInputLocalState]);


  return (
    <div
      className={classes.txnOppUnassignedBin}
      id={`${bin.id}${task.id}`}
    >
      {task.taskType === 'txnOpp' ?
        <ListItem
          button
          className={classes.listItem}
          selected={isSelected}
          classes={{ selected: classes.listItemSelected }}
          onClick={e => handleListItemClick(e, task)}
        >
          {isSelected &&
            <div style={{ height: 72, width: 6, backgroundColor: '#ffa300' }} />
          }
          {taskCompleted ?
          <div className={classes.completed}>
            <SvgIcon htmlColor='white' style={{ fontSize: 18 }}>
              <DoneIcon />
            </SvgIcon>
          </div> :
            <ListItemAvatar className={classes.listItemAvatar}>
              <Avatar className={classNames(classes.queueAvatar, classes.green)}>
                <HomeIcon/>
                <Typography className={ classes.avatarText }/>
              </Avatar>
            </ListItemAvatar>
          }
          <div className={classes.listItemContent}>
            <Grid container wrap='nowrap' spacing={0} alignItems='center' direction='row' justifyContent='space-between'>
              <Grid item className={classes.portfolioNameContainer}>
                <Typography className={classes.textPrimary} component="div">{street}</Typography>
              </Grid>
              <Grid item><Typography component="div">{taskId}</Typography></Grid>
            </Grid>
            <Typography className={classes.textSecondary} component="div">{taskDetails}</Typography>
            <Typography className={classes.textSecondary} component="div">
              <span className={classes.inlinePadding}>{importName}</span>
            </Typography>
            <div className={classes.badgeContainer}>
              <KeyValueBadge small name='Beds' value={beds} />
              <KeyValueBadge small name='Baths' value={baths} />
              <KeyValueBadge small name='Sqft' value={sqft} />
              <div style={{ flexGrow: 1 }} />
              {!showPostAcquisitions &&
                <OpportunityStatusChip txnOpp={task} highlightStatus={highlightStatus} />
              }
            </div>
          </div>
        </ListItem>
        :
        <ListItem
          button
          className={classes.listItem}
          selected={isSelected}
          classes={{ selected: classes.listItemSelected }}
          onClick={e => handleListItemClick(e, task)}
        >
          {isSelected &&
            <div style={{ height: 72, width: 6, backgroundColor: '#ffa300' }} />
          }
          {taskCompleted ?
          <div className={classes.completed}>
            <SvgIcon htmlColor='white' style={{ fontSize: 18 }}>
              <DoneIcon />
            </SvgIcon>
          </div> :
            <ListItemAvatar className={classes.listItemAvatar}>
              <Avatar className={classNames(classes.queueAvatar, classes.green)}>
                <HomeIcon/>
                <Typography className={ classes.avatarText }/>
              </Avatar>
            </ListItemAvatar>
          }
          <div className={classes.listItemContent}>
            <Grid container wrap='nowrap' spacing={0} alignItems='center' direction='row' justifyContent='space-between'>
              <Grid item className={classes.portfolioNameContainer}>
                <Typography className={classes.textPrimary} component="div">{street}</Typography>
              </Grid>
              <Grid item><Typography component="div">{taskId}</Typography></Grid>
            </Grid>
            <Typography className={classes.textSecondary} component="div">{taskDetails}</Typography>
            <Typography className={classes.textSecondary} component="div">
              <span className={classes.inlinePadding}>{module === Constants.Module.POSTACQ ? taskType : importName}</span>
            </Typography>
            <div className={classes.badgeContainer}>
              <KeyValueBadge small name='Beds' value={beds} />
              <KeyValueBadge small name='Baths' value={baths} />
              <KeyValueBadge small name='Sqft' value={sqft} />
              <div style={{ flexGrow: 1 }} />
              {module === Constants.Module.POSTACQ
                ? <TaskStatusChip task={task} highlightStatus={highlightStatus}/>
                : !showPostAcquisitions &&
                  <OpportunityStatusChip txnOpp={task} highlightStatus={highlightStatus} />
              }
            </div>
          </div>
        </ListItem>
      }
    </div>)
    ;
}
const styles = theme => ({
  txnOppUnassignedBin: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 8px'
  },
  textPrimary: {
    fontSize: '.85rem',
    fontWeight: '500',
    display: 'inline-block',
    whiteSpace: 'pre',
  },
  inlinePadding: {
    margin: '0px 5px 0px 0px',
  },
  textSecondary: {
    fontSize: '.75rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    margin: '-3px 0px 0px'
  },
  listItem: {
    padding: 0,
    borderRadius: 4,
    borderBottom: '1px solid #e4e4e4',
    backgroundColor: '#fff',
    overflow: 'hidden'
  },
  listItemContent: {
    padding: '0 8px',
    width: '340px'
  },
  listItemSelected: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)!important'
  },
  listItemAvatar: {
    minWidth: 'unset',
  },
  queueAvatar: {
    height: '72px',
    width: '24px',
    borderRadius: 0
  },
  completed: {
    height: '72px',
    width: '24px',
    backgroundColor: '#31c700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    marginRight: '0px',
    height: 68,
    width: 24,
    borderRadius: 0
  },
  avatarText: {
    fontSize: '1.55rem',
    fontWeight: '500',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  green: {
    backgroundColor: theme.palette.primary.main
  },
  portfolioNameContainer: {
    overflow: 'hidden'
  },
  badgeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '4px'
  },
  inlineText: {
    display: 'inline-block',
    whiteSpace: 'pre',
    fontSize: '.75rem',
  },
  inlineSearchText: {
    display: 'inline-block',
    whiteSpace: 'pre',
    fontWeight: 'bold',
    backgroundColor: 'yellow',
    color: 'black',
    fontSize: '.75rem',
  },
  inlinePrimarySearchText: {
    display: 'inline-block',
    whiteSpace: 'pre',
    fontWeight: 'bold',
    backgroundColor: 'yellow',
  },
});

const mapStateToProps = (state, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  const selectedTxnOpp = module === Constants.Module.POSTACQ ? state.postAcqUWState.postAcqUWClientTask : state.propertyUWState.propertyUWClientTxnOpp;
  const valuationIsDirty = module === Constants.Module.POSTACQ ? state.postAcqUWState.valuationIsDirty : state.propertyUWState.valuationIsDirty;
  return {
    module,
    selectedTxnOpp,
    valuationIsDirty,
    showPostAcquisitions: state.queueState.underwrite.showPostAcquisitions
  }
};
const mapDispatchToProps = (dispatch, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  return {
    setQueueDrawerIsOpen: isOpen => dispatch(setQueueDrawerIsOpen(module, isOpen))
  }
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(QueueTask);
